/**
 * Linking the bar's ingredients to the flavour database (D16), and reading the flavour
 * of one that is linked — or of a house batch, which is never in the flavour database
 * but is made of things that are.
 *
 * ## The link
 *
 * An ingredient's `flavour` names the flavour-database ingredient it is, by
 * the flavour database's spelling. It is the organisation's own data, on its `ING#`
 * item, and the reference dataset is never copied onto it. A link is either
 * `confirmed` by a person or `suggested` by `matchFlavourName`; only the
 * exact and alias matches are good enough to suggest without a second look,
 * and the screens say which is which.
 *
 * ## Matching
 *
 * Bar names and flavour-database names differ in predictable ways: case and
 * accents ("Crème de banane"), the house's own prefix ("AB Orange Bitters"),
 * "Fresh" before a juice, and spelling ("Luxardo Marashino"). The matcher
 * tries, in order and stopping at the first hit:
 *
 *   1. the name as is, case- and accent-insensitively;
 *   2. the flavour database's own alias list (menu wording → its name);
 *   3. the name with house prefixes and filler words dropped, against both;
 *   4. the closest name by letter pairs, if it is close enough;
 *   5. the longest flavour-database name the bar's name contains ("Fever Tree
 *      Soda Water" → Soda water).
 *
 * Whatever it finds is only ever a suggestion until a person confirms it;
 * `how` says how it was found, and only `exact` and `alias` are worth
 * trusting at a glance.
 *
 * A name ending in "Batch" is not matched at all: a house batch is the bar's
 * own blend, and the flavour database's "Aviation" is a cocktail, not Above Board's
 * Aviation Batch. Its flavour comes from its build instead (below).
 *
 * ## A batch's flavour
 *
 * A batch is an ingredient that is also a spec — the workbook writes both
 * under one id. Its profile is its lines' profiles averaged by the volume each
 * puts in, the weighting aroma.ts already uses for a drink. Lines with no
 * flavour data are left out rather than counted as water, so a batch with one
 * unlinked line is a slightly less complete picture, not a paler one; the
 * result says how much of the volume it covers.
 */
import { lineAromaWeight } from "./aroma.js";
import { clampScore, flavourNameKey, withFlavourOverrides, FLAVOUR_FAMILIES, FLAVOUR_NOTE_COUNT, FLAVOUR_TASTES, } from "./flavour.js";
/**
 * Spellings that differ for no reason that matters to flavour: whisky and
 * whiskey are the same thing here, and the misspellings are the bar's own.
 */
const SPELLINGS = [
    [/\bwhiskey\b/g, "whisky"],
    [/\bliquer\b/g, "liqueur"],
    [/\bmaderia\b/g, "madeira"],
];
/** Lowercase, no accents, punctuation to spaces, single-spaced, common spellings unified. */
export function normaliseFlavourName(name) {
    let n = name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, " ")
        .trim();
    for (const [pattern, to] of SPELLINGS)
        n = n.replace(pattern, to);
    return n;
}
/**
 * Words that say whose or how fresh, not what. `ab` and `tf` are Above
 * Board's and Tempus Fugit's prefixes as the bar writes them.
 */
const FILLER = new Set(["ab", "tf", "house", "fresh", "freshly", "squeezed", "the"]);
function tidy(normalised) {
    return normalised
        .split(" ")
        .filter((w) => !FILLER.has(w))
        .join(" ");
}
function bigrams(s) {
    const compact = ` ${s} `;
    const out = new Set();
    for (let i = 0; i < compact.length - 1; i++)
        out.add(compact.slice(i, i + 2));
    return out;
}
/** Dice similarity of two letter-pair sets, 0–1. */
function dice(a, b) {
    if (a.size === 0 || b.size === 0)
        return 0;
    let shared = 0;
    for (const g of a)
        if (b.has(g))
            shared++;
    return (2 * shared) / (a.size + b.size);
}
/** Build the index once per name list; matching then costs a pass over it. */
export function flavourNameIndex(names, aliases) {
    const byName = new Map();
    for (const n of names)
        byName.set(normaliseFlavourName(n), n);
    const byAlias = new Map();
    const entries = aliases instanceof Map ? [...aliases.entries()] : Object.entries(aliases);
    for (const [wording, name] of entries)
        byAlias.set(normaliseFlavourName(wording), name);
    return {
        names: byName,
        aliases: byAlias,
        bigrams: [...byName.entries()].map(([k, v]) => [v, bigrams(k)]),
    };
}
/** How close a name must be to suggest it by spelling alone. */
export const CLOSE_MATCH = 0.82;
/** True for a house batch, which is blended from its build rather than matched. */
export function isBatchName(name) {
    return /\bbatch(\s*\d+)?\s*$/i.test(name.trim());
}
/** The flavour-database ingredient a bar's name most likely is, or null. See the module note. */
export function matchFlavourName(name, index) {
    if (isBatchName(name))
        return null;
    // A bracketed note is the bar's, not the product's: "Sugar Syrup (AB 2:1)".
    const n = normaliseFlavourName(name.replace(/\([^)]*\)/g, " ")) || normaliseFlavourName(name);
    if (!n)
        return null;
    const exact = index.names.get(n);
    if (exact)
        return { ref: exact, how: "exact", score: 1 };
    const alias = index.aliases.get(n);
    if (alias)
        return { ref: alias, how: "alias", score: 1 };
    const t = tidy(n);
    if (t && t !== n) {
        const hit = index.names.get(t) ?? index.aliases.get(t);
        if (hit)
            return { ref: hit, how: "tidied", score: 1 };
    }
    const mine = bigrams(t || n);
    let best = null;
    for (const [ref, theirs] of index.bigrams) {
        const score = dice(mine, theirs);
        if (score >= CLOSE_MATCH && (!best || score > best.score))
            best = { ref, how: "close", score };
    }
    return best ?? containedMatch(t || n, index);
}
/**
 * The most specific flavour-database name whose words all appear, in order, in
 * the bar's name: "Fever Tree Soda Water" is Soda water, "Nakano Umeshu" is
 * Umeshu. Longest wins, because "Rye whisky" says more than "Whisky"; on a
 * tie the later words win, because the thing comes after the brand ("Fever
 * Tree Soda Water" is soda water). One short word on its own ("Gin", "Ice")
 * is too easy to find by accident, so a contained name must be two words or
 * six letters.
 */
function containedMatch(normalised, index) {
    const padded = ` ${normalised} `;
    let best = null;
    for (const [key, ref] of index.names) {
        if (key.length < 6 && !key.includes(" "))
            continue;
        const at = padded.lastIndexOf(` ${key} `);
        if (at < 0)
            continue;
        if (!best || key.length > best.length || (key.length === best.length && at > best.at)) {
            best = { ref, length: key.length, at };
        }
    }
    return best ? { ref: best.ref, how: "contains", score: best.length / normalised.length } : null;
}
/**
 * What one ingredient tastes of, if anything can say: its own link (with the
 * org's overrides laid over it), or — for a batch — its build blended.
 *
 * `profiles` holds the flavour database's profiles keyed by `flavourNameKey`, fetched
 * by the caller for every `ref` it will need (`flavourRefsFor`). A suggested
 * link is used like a confirmed one: it is the best answer there is, and the
 * screens mark it.
 */
export function resolveFlavour(ingredientId, ingredients, specs, profiles, seen = new Set()) {
    const ing = ingredients.get(ingredientId);
    if (!ing || ing.kind === "dilution" || seen.has(ingredientId))
        return null;
    if (ing.flavour) {
        const profile = profiles.get(flavourNameKey(ing.flavour.ref));
        if (profile)
            return { profile: withFlavourOverrides(profile, ing.flavour.overrides), from: "link", coverage: 1 };
    }
    // A house-made ingredient's build (#180), or — through the transition — a
    // batch spec of the same id.
    const build = ing.build ?? specs.get(ingredientId);
    if (!build || build.lines.length === 0)
        return null;
    const inner = new Set(seen).add(ingredientId);
    const parts = [];
    let total = 0;
    for (const line of build.lines) {
        const lineIng = ingredients.get(line.ingredientId);
        if (!lineIng || lineIng.kind === "dilution")
            continue;
        const weight = lineAromaWeight(line);
        if (weight === 0)
            continue;
        total += weight;
        const resolved = resolveFlavour(line.ingredientId, ingredients, specs, profiles, inner);
        if (resolved)
            parts.push({ profile: resolved.profile, weight });
    }
    if (parts.length === 0)
        return null;
    const covered = parts.reduce((t, p) => t + p.weight, 0);
    return { profile: blendFlavourProfiles(ing.name, parts), from: "batch", coverage: total > 0 ? covered / total : 0 };
}
/**
 * Every flavour-database name `resolveFlavour` could need for these ingredients:
 * their own links, and the links of everything inside any batch among them.
 */
export function flavourRefsFor(ingredientIds, ingredients, specs) {
    const refs = new Set();
    const seen = new Set();
    const visit = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        const ing = ingredients.get(id);
        if (ing?.flavour)
            refs.add(ing.flavour.ref);
        for (const line of ing?.build?.lines ?? specs.get(id)?.lines ?? [])
            visit(line.ingredientId);
    };
    ingredientIds.forEach(visit);
    return [...refs];
}
/** Profiles averaged by weight, note by note, family by family. */
export function blendFlavourProfiles(name, parts) {
    const total = parts.reduce((t, p) => t + Math.max(0, p.weight), 0);
    const mix = (pick, length) => Array.from({ length }, (_, i) => total > 0
        ? Math.round((parts.reduce((t, p) => t + clampScore(pick(p.profile)[i]) * Math.max(0, p.weight), 0) / total) * 100) / 100
        : 0);
    return {
        name,
        notes: mix((p) => p.notes, FLAVOUR_NOTE_COUNT),
        families: mix((p) => p.families, FLAVOUR_FAMILIES.length),
        tastes: mix((p) => p.tastes, FLAVOUR_TASTES.length),
    };
}
//# sourceMappingURL=flavour-link.js.map